import type { Candle } from '../../domain/types'
import { clamp, mean, stdDev } from './statistics'
import { drawdown } from './risk'

export type Series = (number | null)[]
export type SignalDirection = 'bullish' | 'bearish' | 'neutral'

export interface Signal {
  name: string
  direction: SignalDirection
  strength: number
  value: number
  note: string
}

export function lastNonNull(series: Series): number | null {
  for (let i = series.length - 1; i >= 0; i--) {
    const v = series[i]
    if (v !== null && Number.isFinite(v)) return v
  }
  return null
}

export function sma(values: readonly number[], period: number): Series {
  const out: Series = new Array(values.length).fill(null)
  if (period <= 0) return out
  let windowSum = 0
  for (let i = 0; i < values.length; i++) {
    windowSum += values[i]
    if (i >= period) windowSum -= values[i - period]
    if (i >= period - 1) out[i] = windowSum / period
  }
  return out
}

export function ema(values: readonly number[], period: number): Series {
  const out: Series = new Array(values.length).fill(null)
  if (period <= 0 || values.length < period) return out
  const k = 2 / (period + 1)
  let seed = 0
  for (let i = 0; i < period; i++) seed += values[i]
  let prev = seed / period
  out[period - 1] = prev
  for (let i = period; i < values.length; i++) {
    prev = values[i] * k + prev * (1 - k)
    out[i] = prev
  }
  return out
}

// Wilder's RSI: smoothed average gains/losses over the period.
export function rsi(closes: readonly number[], period = 14): Series {
  const out: Series = new Array(closes.length).fill(null)
  if (closes.length <= period) return out
  let gain = 0
  let loss = 0
  for (let i = 1; i <= period; i++) {
    const change = closes[i] - closes[i - 1]
    if (change >= 0) gain += change
    else loss -= change
  }
  let avgGain = gain / period
  let avgLoss = loss / period
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss)
  for (let i = period + 1; i < closes.length; i++) {
    const change = closes[i] - closes[i - 1]
    const up = change > 0 ? change : 0
    const down = change < 0 ? -change : 0
    avgGain = (avgGain * (period - 1) + up) / period
    avgLoss = (avgLoss * (period - 1) + down) / period
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss)
  }
  return out
}

export interface MacdResult {
  macd: Series
  signal: Series
  histogram: Series
}

export function macd(closes: readonly number[], fast = 12, slow = 26, signalPeriod = 9): MacdResult {
  const emaFast = ema(closes, fast)
  const emaSlow = ema(closes, slow)
  const macdLine: Series = closes.map((_, i) => {
    const f = emaFast[i]
    const s = emaSlow[i]
    return f !== null && s !== null ? f - s : null
  })
  const macdValues = macdLine.map((v) => (v === null ? 0 : v))
  const firstIdx = macdLine.findIndex((v) => v !== null)
  const signalRaw = ema(macdValues.slice(Math.max(firstIdx, 0)), signalPeriod)
  const signal: Series = new Array(closes.length).fill(null)
  if (firstIdx >= 0) {
    for (let i = 0; i < signalRaw.length; i++) signal[firstIdx + i] = signalRaw[i]
  }
  const histogram: Series = closes.map((_, i) => {
    const m = macdLine[i]
    const s = signal[i]
    return m !== null && s !== null ? m - s : null
  })
  return { macd: macdLine, signal, histogram }
}

export function trueRange(candles: readonly Candle[]): number[] {
  const out: number[] = []
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i]
    if (i === 0) {
      out.push(c.high - c.low)
    } else {
      const prevClose = candles[i - 1].close
      out.push(Math.max(c.high - c.low, Math.abs(c.high - prevClose), Math.abs(c.low - prevClose)))
    }
  }
  return out
}

export function atr(candles: readonly Candle[], period = 14): Series {
  const tr = trueRange(candles)
  const out: Series = new Array(candles.length).fill(null)
  if (candles.length <= period) return out
  let seed = 0
  for (let i = 1; i <= period; i++) seed += tr[i]
  let prev = seed / period
  out[period] = prev
  for (let i = period + 1; i < candles.length; i++) {
    prev = (prev * (period - 1) + tr[i]) / period
    out[i] = prev
  }
  return out
}

export function rollingVolatility(closes: readonly number[], period = 20, annualize = true): Series {
  const out: Series = new Array(closes.length).fill(null)
  const rets: number[] = []
  for (let i = 1; i < closes.length; i++) {
    rets.push(closes[i - 1] > 0 ? Math.log(closes[i] / closes[i - 1]) : 0)
  }
  const factor = annualize ? Math.sqrt(252) : 1
  for (let i = period; i < closes.length; i++) {
    const window = rets.slice(i - period, i)
    out[i] = stdDev(window) * factor
  }
  return out
}

export interface BollingerBands {
  middle: Series
  upper: Series
  lower: Series
}

export function bollingerBands(closes: readonly number[], period = 20, mult = 2): BollingerBands {
  const middle = sma(closes, period)
  const upper: Series = new Array(closes.length).fill(null)
  const lower: Series = new Array(closes.length).fill(null)
  for (let i = period - 1; i < closes.length; i++) {
    const window = closes.slice(i - period + 1, i + 1)
    const sd = stdDev(window, false)
    const m = middle[i]
    if (m !== null) {
      upper[i] = m + mult * sd
      lower[i] = m - mult * sd
    }
  }
  return { middle, upper, lower }
}

export function linregSlope(values: readonly number[]): number {
  const n = values.length
  if (n < 2) return 0
  const xs = values.map((_, i) => i)
  const mx = mean(xs)
  const my = mean(values)
  let num = 0
  let den = 0
  for (let i = 0; i < n; i++) {
    num += (xs[i] - mx) * (values[i] - my)
    den += (xs[i] - mx) ** 2
  }
  return den === 0 ? 0 : num / den
}

export interface SupportResistance {
  supports: number[]
  resistances: number[]
  nearestSupport: number | null
  nearestResistance: number | null
}

export function supportResistance(candles: readonly Candle[], window = 3): SupportResistance {
  const pivLows: number[] = []
  const pivHighs: number[] = []
  for (let i = window; i < candles.length - window; i++) {
    const slice = candles.slice(i - window, i + window + 1)
    const low = candles[i].low
    const high = candles[i].high
    if (slice.every((c) => low <= c.low)) pivLows.push(low)
    if (slice.every((c) => high >= c.high)) pivHighs.push(high)
  }
  const price = candles.length ? candles[candles.length - 1].close : 0
  const supports = dedupeLevels(pivLows.filter((l) => l <= price)).sort((a, b) => b - a)
  const resistances = dedupeLevels(pivHighs.filter((h) => h >= price)).sort((a, b) => a - b)
  return {
    supports,
    resistances,
    nearestSupport: supports.length ? supports[0] : null,
    nearestResistance: resistances.length ? resistances[0] : null,
  }
}

function dedupeLevels(levels: number[], tolerance = 0.01): number[] {
  const sorted = [...levels].sort((a, b) => a - b)
  const out: number[] = []
  for (const lvl of sorted) {
    const last = out[out.length - 1]
    if (last === undefined || Math.abs(lvl - last) / Math.max(last, 1e-9) > tolerance) out.push(lvl)
  }
  return out
}

export interface Gap {
  index: number
  time: string
  gapPct: number
  direction: 'up' | 'down'
  filled: boolean
}

export function detectGaps(candles: readonly Candle[], thresholdPct = 0.02): Gap[] {
  const gaps: Gap[] = []
  for (let i = 1; i < candles.length; i++) {
    const prevClose = candles[i - 1].close
    if (prevClose <= 0) continue
    const gapPct = (candles[i].open - prevClose) / prevClose
    if (Math.abs(gapPct) < thresholdPct) continue
    const direction = gapPct > 0 ? 'up' : 'down'
    let filled = false
    for (let j = i; j < candles.length; j++) {
      if (direction === 'up' && candles[j].low <= prevClose) filled = true
      if (direction === 'down' && candles[j].high >= prevClose) filled = true
      if (filled) break
    }
    gaps.push({ index: i, time: candles[i].time, gapPct, direction, filled })
  }
  return gaps
}

export type MarketRegime = 'bull-trend' | 'bear-trend' | 'range' | 'high-volatility' | 'low-liquidity' | 'deep-drawdown'

export interface MarketRegimeResult {
  regime: MarketRegime
  trend: Trend
  volatility: number
  avgDollarVolume: number
  drawdownPct: number
  description: string
}

export interface StockRiskWarning {
  id: string
  severity: 'warn' | 'danger'
  message: string
}

export interface StockWatchAlert {
  id: string
  tone: 'green' | 'amber' | 'red' | 'gray'
  message: string
}

export interface StockRiskThresholds {
  maxAnnualVolatility?: number
  minAvgDollarVolume?: number
  maxCurrentDrawdownPct?: number
  minLastVolumeRatio?: number
}

export function detectMarketRegime(candles: readonly Candle[], thresholds: StockRiskThresholds = {}): MarketRegimeResult {
  const closes = candles.map((c) => c.close)
  const trend = classifyTrend(closes)
  const vol = lastNonNull(rollingVolatility(closes, 20)) ?? 0
  const recent = candles.slice(-20)
  const avgDollarVolume = mean(recent.map((c) => c.close * c.volume))
  const dd = drawdown(closes)
  const maxVol = thresholds.maxAnnualVolatility ?? 0.65
  const minDollarVolume = thresholds.minAvgDollarVolume ?? 5_000_000
  const maxDd = thresholds.maxCurrentDrawdownPct ?? 0.18

  let regime: MarketRegime
  if (avgDollarVolume < minDollarVolume) regime = 'low-liquidity'
  else if (vol > maxVol) regime = 'high-volatility'
  else if (dd.currentDrawdownPct > maxDd) regime = 'deep-drawdown'
  else if (trend.trend === 'uptrend') regime = 'bull-trend'
  else if (trend.trend === 'downtrend') regime = 'bear-trend'
  else regime = 'range'

  return {
    regime,
    trend: trend.trend,
    volatility: vol,
    avgDollarVolume,
    drawdownPct: dd.currentDrawdownPct,
    description: regimeDescription(regime),
  }
}

export function stockRiskWarnings(candles: readonly Candle[], thresholds: StockRiskThresholds = {}): StockRiskWarning[] {
  if (candles.length < 30) {
    return [{ id: 'sample', severity: 'danger', message: 'Do not trade this: too little price history to evaluate regime, volatility, or drawdown risk.' }]
  }
  const closes = candles.map((c) => c.close)
  const recent = candles.slice(-20)
  const avgVolume = mean(recent.map((c) => c.volume))
  const last = candles[candles.length - 1]
  const vol = lastNonNull(rollingVolatility(closes, 20)) ?? 0
  const avgDollarVolume = mean(recent.map((c) => c.close * c.volume))
  const dd = drawdown(closes)
  const warnings: StockRiskWarning[] = []
  const maxVol = thresholds.maxAnnualVolatility ?? 0.65
  const minDollarVolume = thresholds.minAvgDollarVolume ?? 5_000_000
  const maxDd = thresholds.maxCurrentDrawdownPct ?? 0.18
  const minVolRatio = thresholds.minLastVolumeRatio ?? 0.35

  if (vol > maxVol) warnings.push({ id: 'volatility', severity: 'danger', message: `Do not trade this: annualized volatility is ${(vol * 100).toFixed(0)}%, above the ${(maxVol * 100).toFixed(0)}% limit.` })
  if (avgDollarVolume < minDollarVolume) warnings.push({ id: 'liquidity', severity: 'danger', message: `Do not trade this: average dollar volume is below ${Math.round(minDollarVolume).toLocaleString('en-US')}.` })
  if (dd.currentDrawdownPct > maxDd) warnings.push({ id: 'drawdown', severity: 'danger', message: `Do not trade this: current drawdown is ${(dd.currentDrawdownPct * 100).toFixed(1)}%, above the ${(maxDd * 100).toFixed(0)}% risk limit.` })
  if (avgVolume > 0 && last.volume / avgVolume < minVolRatio) warnings.push({ id: 'thin-tape', severity: 'warn', message: 'Volume is far below its recent average; fills and signals may be unreliable.' })

  return warnings
}

export function stockWatchAlerts(candles: readonly Candle[]): StockWatchAlert[] {
  if (candles.length < 30) return []
  const bundle = computeIndicators(candles)
  const last = candles[candles.length - 1]
  const prev = candles[candles.length - 2] ?? last
  const alerts: StockWatchAlert[] = []
  const rsiVal = lastNonNull(bundle.rsi14)
  const avgVolume = mean(candles.slice(-20).map((c) => c.volume))
  const lastSma20 = lastNonNull(bundle.sma20)
  const lastSma50 = lastNonNull(bundle.sma50)
  const prevSma20 = previousNonNull(bundle.sma20)
  const prevSma50 = previousNonNull(bundle.sma50)

  if (rsiVal !== null && rsiVal < 30) alerts.push({ id: 'rsi-oversold', tone: 'green', message: `RSI is oversold at ${rsiVal.toFixed(0)}.` })
  if (rsiVal !== null && rsiVal > 70) alerts.push({ id: 'rsi-overbought', tone: 'red', message: `RSI is overbought at ${rsiVal.toFixed(0)}.` })
  if (avgVolume > 0 && last.volume / avgVolume >= 1.8) alerts.push({ id: 'volume-spike', tone: 'amber', message: `Volume spike: ${(last.volume / avgVolume).toFixed(1)}x 20-day average.` })
  if (bundle.supportResistance.nearestResistance && last.close > bundle.supportResistance.nearestResistance) alerts.push({ id: 'resistance-break', tone: 'green', message: 'Price closed above nearest resistance.' })
  if (bundle.supportResistance.nearestSupport && last.close < bundle.supportResistance.nearestSupport) alerts.push({ id: 'support-break', tone: 'red', message: 'Price closed below nearest support.' })
  if (lastSma20 !== null && lastSma50 !== null && prevSma20 !== null && prevSma50 !== null && prevSma20 <= prevSma50 && lastSma20 > lastSma50) {
    alerts.push({ id: 'ma-cross-up', tone: 'green', message: 'SMA 20 crossed above SMA 50.' })
  }
  if (lastSma20 !== null && lastSma50 !== null && prevSma20 !== null && prevSma50 !== null && prevSma20 >= prevSma50 && lastSma20 < lastSma50) {
    alerts.push({ id: 'ma-cross-down', tone: 'red', message: 'SMA 20 crossed below SMA 50.' })
  }
  const gapPct = prev.close > 0 ? (last.open - prev.close) / prev.close : 0
  if (Math.abs(gapPct) >= 0.02) alerts.push({ id: 'fresh-gap', tone: gapPct > 0 ? 'green' : 'red', message: `Fresh ${gapPct > 0 ? 'up' : 'down'} gap of ${(gapPct * 100).toFixed(1)}%.` })

  return alerts
}

export type Trend = 'uptrend' | 'downtrend' | 'sideways'

export interface TrendClassification {
  trend: Trend
  strength: number
  normalizedSlope: number
}

export function classifyTrend(closes: readonly number[], lookback = 50): TrendClassification {
  const window = closes.slice(-lookback)
  if (window.length < 3) return { trend: 'sideways', strength: 0, normalizedSlope: 0 }
  const slope = linregSlope(window)
  const avg = mean(window)
  const normalizedSlope = avg > 0 ? (slope * window.length) / avg : 0
  const strength = clamp(Math.abs(normalizedSlope) * 5, 0, 1)
  let trend: Trend = 'sideways'
  if (normalizedSlope > 0.02) trend = 'uptrend'
  else if (normalizedSlope < -0.02) trend = 'downtrend'
  return { trend, strength, normalizedSlope }
}

export function meanReversionSignal(closes: readonly number[], period = 20): Signal {
  const window = closes.slice(-period)
  const price = closes[closes.length - 1] ?? 0
  const m = mean(window)
  const sd = stdDev(window)
  const z = sd > 0 ? (price - m) / sd : 0
  let direction: SignalDirection = 'neutral'
  if (z > 1) direction = 'bearish'
  else if (z < -1) direction = 'bullish'
  return {
    name: 'Mean reversion',
    direction,
    strength: clamp(Math.abs(z) / 3, 0, 1),
    value: z,
    note:
      direction === 'neutral'
        ? `Price is within 1σ of its ${period}-period mean (z=${z.toFixed(2)}). No reversion edge.`
        : `Price is ${z > 0 ? 'stretched above' : 'stretched below'} the mean (z=${z.toFixed(2)}); reversion ${direction === 'bullish' ? 'higher' : 'lower'} is statistically favoured but not guaranteed.`,
  }
}

export function momentumSignal(closes: readonly number[], period = 20): Signal {
  if (closes.length <= period) {
    return { name: 'Momentum', direction: 'neutral', strength: 0, value: 0, note: 'Not enough data.' }
  }
  const price = closes[closes.length - 1]
  const past = closes[closes.length - 1 - period]
  const roc = past > 0 ? (price - past) / past : 0
  let direction: SignalDirection = 'neutral'
  if (roc > 0.03) direction = 'bullish'
  else if (roc < -0.03) direction = 'bearish'
  return {
    name: 'Momentum',
    direction,
    strength: clamp(Math.abs(roc) * 5, 0, 1),
    value: roc,
    note: `${period}-period rate of change is ${(roc * 100).toFixed(1)}%. Momentum can persist but reverses sharply at turns.`,
  }
}

export interface IndicatorBundle {
  closes: number[]
  sma20: Series
  sma50: Series
  sma200: Series
  ema20: Series
  rsi14: Series
  macd: MacdResult
  atr14: Series
  volatility: Series
  bollinger: BollingerBands
  trend: TrendClassification
  supportResistance: SupportResistance
  gaps: Gap[]
  meanReversion: Signal
  momentum: Signal
  regime: MarketRegimeResult
  riskWarnings: StockRiskWarning[]
  watchAlerts: StockWatchAlert[]
}

export function computeIndicators(candles: readonly Candle[]): IndicatorBundle {
  const closes = candles.map((c) => c.close)
  return {
    closes,
    sma20: sma(closes, 20),
    sma50: sma(closes, 50),
    sma200: sma(closes, 200),
    ema20: ema(closes, 20),
    rsi14: rsi(closes, 14),
    macd: macd(closes),
    atr14: atr(candles, 14),
    volatility: rollingVolatility(closes, 20),
    bollinger: bollingerBands(closes),
    trend: classifyTrend(closes),
    supportResistance: supportResistance(candles),
    gaps: detectGaps(candles),
    meanReversion: meanReversionSignal(closes),
    momentum: momentumSignal(closes),
    regime: detectMarketRegime(candles),
    riskWarnings: stockRiskWarnings(candles),
    watchAlerts: stockWatchAlertsRaw(candles, closes),
  }
}

function previousNonNull(series: Series): number | null {
  let foundLast = false
  for (let i = series.length - 1; i >= 0; i--) {
    const v = series[i]
    if (v === null || !Number.isFinite(v)) continue
    if (!foundLast) {
      foundLast = true
      continue
    }
    return v
  }
  return null
}

function regimeDescription(regime: MarketRegime): string {
  switch (regime) {
    case 'bull-trend':
      return 'Uptrend with acceptable volatility and liquidity.'
    case 'bear-trend':
      return 'Downtrend with acceptable volatility and liquidity.'
    case 'range':
      return 'Sideways regime; mean-reversion signals may fit better than trend-following.'
    case 'high-volatility':
      return 'Volatility is elevated; reduce size or stand down.'
    case 'low-liquidity':
      return 'Liquidity is weak; spreads and slippage can dominate the signal.'
    case 'deep-drawdown':
      return 'Current drawdown is deep; trend-following entries need extra caution.'
  }
}

function stockWatchAlertsRaw(candles: readonly Candle[], closes: readonly number[]): StockWatchAlert[] {
  if (candles.length < 30) return []
  const rsi14 = rsi(closes, 14)
  const sma20 = sma(closes, 20)
  const sma50 = sma(closes, 50)
  const sr = supportResistance(candles)
  const last = candles[candles.length - 1]
  const prev = candles[candles.length - 2] ?? last
  const alerts: StockWatchAlert[] = []
  const rsiVal = lastNonNull(rsi14)
  const avgVolume = mean(candles.slice(-20).map((c) => c.volume))
  const lastSma20 = lastNonNull(sma20)
  const lastSma50 = lastNonNull(sma50)
  const prevSma20 = previousNonNull(sma20)
  const prevSma50 = previousNonNull(sma50)

  if (rsiVal !== null && rsiVal < 30) alerts.push({ id: 'rsi-oversold', tone: 'green', message: `RSI is oversold at ${rsiVal.toFixed(0)}.` })
  if (rsiVal !== null && rsiVal > 70) alerts.push({ id: 'rsi-overbought', tone: 'red', message: `RSI is overbought at ${rsiVal.toFixed(0)}.` })
  if (avgVolume > 0 && last.volume / avgVolume >= 1.8) alerts.push({ id: 'volume-spike', tone: 'amber', message: `Volume spike: ${(last.volume / avgVolume).toFixed(1)}x 20-day average.` })
  if (sr.nearestResistance && last.close > sr.nearestResistance) alerts.push({ id: 'resistance-break', tone: 'green', message: 'Price closed above nearest resistance.' })
  if (sr.nearestSupport && last.close < sr.nearestSupport) alerts.push({ id: 'support-break', tone: 'red', message: 'Price closed below nearest support.' })
  if (lastSma20 !== null && lastSma50 !== null && prevSma20 !== null && prevSma50 !== null && prevSma20 <= prevSma50 && lastSma20 > lastSma50) {
    alerts.push({ id: 'ma-cross-up', tone: 'green', message: 'SMA 20 crossed above SMA 50.' })
  }
  if (lastSma20 !== null && lastSma50 !== null && prevSma20 !== null && prevSma50 !== null && prevSma20 >= prevSma50 && lastSma20 < lastSma50) {
    alerts.push({ id: 'ma-cross-down', tone: 'red', message: 'SMA 20 crossed below SMA 50.' })
  }
  const gapPct = prev.close > 0 ? (last.open - prev.close) / prev.close : 0
  if (Math.abs(gapPct) >= 0.02) alerts.push({ id: 'fresh-gap', tone: gapPct > 0 ? 'green' : 'red', message: `Fresh ${gapPct > 0 ? 'up' : 'down'} gap of ${(gapPct * 100).toFixed(1)}%.` })

  return alerts
}
